/**
 * Path-based router for the Hype admin SPA.
 *
 * Routes are matched against `window.location.pathname` (e.g. "/countdown").
 * Listeners receive the matched route name on every navigation.
 *
 * App Bridge CDN auto-detects history.pushState() calls and syncs the admin
 * URL + s-app-nav active state — no manual pathname/popstate workarounds needed.
 *
 * No external library — keeps the bundle small and the logic auditable.
 */

export type RouteName =
  | 'dashboard'
  | 'countdown'
  | 'sales-popup'
  | 'stock-counter'
  | 'announcement-bar'
  | 'visitor-counter'
  | 'analytics'
  | 'billing';

export interface Route {
  path: string;       // e.g. "/countdown"
  name: RouteName;
  title: string;      // Human-readable, used for App Bridge TitleBar
  navLabel: string;   // Sidebar label
}

export const ROUTES: Route[] = [
  { path: '/',                name: 'dashboard',        title: 'Dashboard',        navLabel: 'Dashboard' },
  { path: '/countdown',      name: 'countdown',        title: 'Countdown Timer',  navLabel: 'Countdown Timer' },
  { path: '/sales-popup',    name: 'sales-popup',      title: 'Sales Popup',      navLabel: 'Sales Popup' },
  { path: '/stock-counter',  name: 'stock-counter',    title: 'Stock Counter',    navLabel: 'Stock Counter' },
  { path: '/announcement-bar', name: 'announcement-bar', title: 'Announcement Bar', navLabel: 'Announcement Bar' },
  { path: '/visitor-counter', name: 'visitor-counter',  title: 'Visitor Counter',  navLabel: 'Visitor Counter' },
  { path: '/analytics',      name: 'analytics',        title: 'Analytics',        navLabel: 'Analytics' },
  { path: '/billing',        name: 'billing',          title: 'Billing',          navLabel: 'Billing' },
];

const DEFAULT_ROUTE = ROUTES[0];

export type RouteChangeListener = (route: Route) => void;

export class Router {
  private listeners: Set<RouteChangeListener> = new Set();
  private _current: Route = DEFAULT_ROUTE;

  constructor() {
    // Handle forward/back navigation (popstate fires on browser back/forward).
    window.addEventListener('popstate', () => this._resolve());
    // Handle initial page load — resolve the current pathname.
    this._resolve();
  }

  get current(): Route {
    return this._current;
  }

  /** Navigate to a route by name. */
  navigate(name: RouteName): void {
    const route = ROUTES.find(r => r.name === name);
    if (!route) return;
    // pushState does NOT fire popstate, so call _resolve() manually.
    history.pushState(null, '', route.path);
    this._resolve();
  }

  /** Subscribe to route changes. Returns an unsubscribe function. */
  subscribe(listener: RouteChangeListener): () => void {
    this.listeners.add(listener);
    // Emit current route immediately so callers can initialise.
    listener(this._current);
    return () => this.listeners.delete(listener);
  }

  private _resolve(): void {
    const pathname = window.location.pathname;
    const route = ROUTES.find(r => r.path === pathname) ?? DEFAULT_ROUTE;

    this._current = route;
    this.listeners.forEach(l => l(route));
  }
}

/** Singleton router instance shared across the app. */
export const router = new Router();
